/**
 * Isometric projection (2:1 diamond). Pure functions, no rendering dependency —
 * both the renderer and picking/camera code share these transforms so screen and
 * world stay in perfect agreement.
 *
 *   grid (tile x,y, elevation)  <->  world screen (px)
 *
 * World space here is "un-zoomed, un-panned" pixel space with the origin at the
 * top corner of tile (0,0). The camera applies zoom + translation on top.
 */
import { TILE_HALF_WIDTH, TILE_HALF_HEIGHT, ELEVATION_STEP } from '../constants.js';
import type { Vec2 } from './vec2.js';

/** Project a tile coordinate (+ optional elevation) to world-pixel space. */
export const gridToWorld = (tileX: number, tileY: number, elevation = 0): Vec2 => ({
  x: (tileX - tileY) * TILE_HALF_WIDTH,
  y: (tileX + tileY) * TILE_HALF_HEIGHT - elevation * ELEVATION_STEP,
});

/**
 * Inverse projection: world-pixel space back to fractional tile coordinates
 * (ignoring elevation — callers resolve height separately via the map). Floor the
 * result to get the tile under a point.
 */
export const worldToGrid = (worldX: number, worldY: number): Vec2 => {
  const a = worldX / TILE_HALF_WIDTH;
  const b = worldY / TILE_HALF_HEIGHT;
  return {
    x: (a + b) / 2,
    y: (b - a) / 2,
  };
};

/** Depth key for painter's-algorithm sorting: higher = drawn later (in front). */
export const isoDepth = (tileX: number, tileY: number, elevation = 0): number =>
  (tileX + tileY) * 16 + elevation;
